// src/services/sendReminderService.js
import api from './api';

// Send Reminder API Service
export const sendReminderAPI = {
  // Get surveys (filtered by user role)
  getSurveys: () => {
    return api.get('/SendSurvey/surveys');
  },

  // Get pending invitations for a survey
  getPendingInvitations: (surveyId) => {
    return api.get(`/SendSurvey/pending-invitations/${surveyId}`);
  },

  // Send reminder to specific invitation
  sendReminder: (invitationId) => {
    return api.post(`/SendSurvey/send-reminder/${invitationId}`);
  },

  // Send reminders for all pending invitations in a survey.
  // NOTE: the server sends each pending invitation's reminder email
  // synchronously, one by one, before responding, so this can legitimately
  // take a while for a survey with many pending invitations -- the default
  // 15s timeout (see api.js TIMEOUT_CONFIG.default) isn't long enough once
  // real SMTP sends are involved. Bumped here for the same reason as
  // sendSurveyService's /SendSurvey/send call.
  sendRemindersForSurvey: (surveyId) => {
    return api.post(`/SendSurvey/send-reminders/${surveyId}`, null, { timeout: 120000 });
  },

  // Send reminders to all pending invitations (Super Admin only) -- same
  // "many synchronous emails in one request" timeout consideration as above.
  sendAllReminders: () => {
    return api.post('/SendSurvey/send-all-reminders', null, { timeout: 120000 });
  },

  // Get reminder statistics
  getReminderStats: (surveyId) => {
    return api.get(`/SendSurvey/reminder-stats/${surveyId}`);
  },

  // Get invitation details
  getInvitationDetails: (invitationId) => {
    return api.get(`/SendSurvey/invitation-details/${invitationId}`);
  }
};

// Enhanced methods with error handling and retry logic
export const enhancedSendReminderService = {
  ...sendReminderAPI,

  // WARNING: safe to use for sendReminder (a single invitation) since that
  // call only affects one row/one email. Do NOT wire this up for
  // sendRemindersForSurvey / sendAllReminders -- those are not idempotent
  // (each call re-sends every pending invitation's email again), so retrying
  // one on a timeout would re-send the whole batch, same issue documented in
  // sendSurveyService.js / SendSurvey.jsx.
  // Send reminder with retry logic
  sendReminderWithRetry: async (invitationId, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📤 Attempt ${attempt} to send reminder for invitation ${invitationId}...`);
        const response = await sendReminderAPI.sendReminder(invitationId);
        console.log(`✅ Reminder sent successfully on attempt ${attempt}`);
        return response;
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          // Exponential backoff
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          console.log(`⏳ Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  },

  // Send reminders in batches
  sendRemindersInBatches: async (invitationIds, batchSize = 10) => {
    const batches = [];
    
    // Split invitation IDs into batches
    for (let i = 0; i < invitationIds.length; i += batchSize) {
      batches.push(invitationIds.slice(i, i + batchSize));
    }

    const results = {
      totalRemindersSent: 0,
      totalFailed: 0,
      batchResults: []
    };

    console.log(`📦 Sending reminders in ${batches.length} batches of ${batchSize} invitations each`);

    for (let i = 0; i < batches.length; i++) {
      try {
        console.log(`📤 Processing batch ${i + 1}/${batches.length}...`);
        
        const batchPromises = batches[i].map(invitationId => 
          sendReminderAPI.sendReminder(invitationId)
        );

        const batchResults = await Promise.allSettled(batchPromises);
        
        const batchSent = batchResults.filter(r => r.status === 'fulfilled' && r.value.data.totalRemindersSent > 0).length;
        const batchFailed = batchResults.filter(r => r.status === 'rejected' || r.value.data.totalFailed > 0).length;

        results.totalRemindersSent += batchSent;
        results.totalFailed += batchFailed;
        
        results.batchResults.push({
          batch: i + 1,
          success: true,
          sent: batchSent,
          failed: batchFailed,
          details: batchResults.map((r, index) => {
            if (r.status === 'fulfilled') {
              return r.value.data.details[0];
            } else {
              return {
                invitationId: batches[i][index],
                success: false,
                message: r.reason?.response?.data?.message || 'Failed to send reminder'
              };
            }
          })
        });

        console.log(`✅ Batch ${i + 1} completed: ${batchSent} sent, ${batchFailed} failed`);

        // Small delay between batches to avoid overwhelming the server
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`❌ Batch ${i + 1} failed:`, error.message);
        results.batchResults.push({
          batch: i + 1,
          success: false,
          error: error.message,
          sent: 0,
          failed: batches[i].length
        });
        results.totalFailed += batches[i].length;
      }
    }

    return { data: results };
  },

  // Validate invitation before sending reminder
  validateInvitation: async (invitationId) => {
    try {
      const response = await sendReminderAPI.getInvitationDetails(invitationId);
      const invitation = response.data;

      if (invitation.isCompleted) {
        return {
          isValid: false,
          error: 'Survey already completed'
        };
      }

      if (invitation.reminderCount >= 10) {
        return {
          isValid: false,
          error: 'Maximum reminder limit reached (10 reminders)'
        };
      }

      // Check if enough time has passed since last reminder
      if (invitation.lastReminderSent) {
        const lastReminder = new Date(invitation.lastReminderSent);
        const now = new Date();
        const hoursSinceLastReminder = (now - lastReminder) / (1000 * 60 * 60);
        
        if (hoursSinceLastReminder < 12) {
          return {
            isValid: false,
            error: `Please wait ${Math.ceil(12 - hoursSinceLastReminder)} more hours before sending next reminder`
          };
        }
      }

      return {
        isValid: true,
        invitation
      };
    } catch (error) {
      return {
        isValid: false,
        error: 'Failed to validate invitation'
      };
    }
  },

  // Get comprehensive reminder statistics
  getComprehensiveStats: async (surveyId) => {
    try {
      const [statsResponse, invitationsResponse] = await Promise.all([
        sendReminderAPI.getReminderStats(surveyId),
        sendReminderAPI.getPendingInvitations(surveyId)
      ]);

      const stats = statsResponse.data;
      const invitations = invitationsResponse.data;

      const completedCount = invitations.filter(inv => inv.isCompleted).length;
      const pendingCount = invitations.filter(inv => !inv.isCompleted).length;
      const totalRemindersSent = invitations.reduce((sum, inv) => sum + inv.reminderCount, 0);
      
      const averageRemindersPerPending = pendingCount > 0 ? (totalRemindersSent / pendingCount).toFixed(1) : 0;
      const completionRate = invitations.length > 0 ? ((completedCount / invitations.length) * 100).toFixed(1) : 0;

      return {
        data: {
          ...stats,
          totalInvitations: invitations.length,
          completedCount,
          pendingCount,
          totalRemindersSent,
          averageRemindersPerPending,
          completionRate: `${completionRate}%`,
          remindersNeeded: pendingCount - totalRemindersSent
        }
      };
    } catch (error) {
      console.error('Error getting comprehensive stats:', error);
      throw error;
    }
  }
};

// Export both services
export default sendReminderAPI;